import { createApi } from '@reduxjs/toolkit/query/react'
import { Export, ExportType } from '../types/export'
import { ListExportsResp, OkResp, QueryExportResp } from '../types/api/responses'
import { DATASET_EXPORT_URL, DATASET_QUERY_EXPORT_URL } from '../constants'
import baseQueryWithReauth from './baseQuery'
import { apiTags } from './apiTags'
import { SimplePaginationReq } from '../types/common'

// Add FileSystem Access API types
declare global {
  interface FileSystemFileHandle {
    createWritable(): Promise<FileSystemWritableFileStream>
  }

  interface FileSystemWritableFileStream extends WritableStream {
    write(data: BufferSource | Blob | string): Promise<void>
    close(): Promise<void>
  }

  interface Window {
    showSaveFilePicker?: (options?: {
      suggestedName?: string
      types?: Array<{
        description: string
        accept: Record<string, string[]>
      }>
    }) => Promise<FileSystemFileHandle>
  }
}

export const exportApi = createApi({
  reducerPath: 'exportApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Export, apiTags.ExportContent, apiTags.ExportMetadata],

  endpoints: (builder) => ({
    listExports: builder.query<ListExportsResp, SimplePaginationReq>({
      query: (params) => ({
        url: DATASET_EXPORT_URL,
        params,
      }),
      providesTags: (result) => [
        { type: apiTags.Export, id: 'LIST' },
        ...(result?.exports?.map(({ id }) => ({
          type: apiTags.Export,
          id,
        })) || []),
      ],
    }),

    createExport: builder.mutation<
      Export,
      {
        name: string
        export_types: ExportType[]
        project_id: string
        dataset_id: string
        model_id?: string
      }
    >({
      query: (body) => ({
        url: DATASET_EXPORT_URL,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => [
        { type: apiTags.Export, id: 'LIST' },
        ...(result?.id
          ? [
              { type: apiTags.Export, id: result.id },
              { type: apiTags.ExportContent, id: result.id },
              { type: apiTags.ExportMetadata, id: result.id },
            ]
          : []),
      ],
    }),

    queryExports: builder.query<
      QueryExportResp,
      {
        filters?: {
          key: string
          regex?: {
            enable: boolean
            options: string
          }
          value: string
        }[]
        operator?: 'and' | 'or' | 'nor'
        limit?: number
        page?: number
        sort_key?: string
        sort_val?: number
      }
    >({
      query: (params) => ({
        url: DATASET_QUERY_EXPORT_URL,
        method: 'POST',
        body: {
          ...params,
          // Only include operator if filters exist and are non-empty
          operator: params.filters?.length ? params.operator || 'and' : undefined,
          // Only include filters if they exist and are non-empty
          filters: params.filters?.length ? params.filters : undefined,
        },
      }),
      providesTags: (result) => [
        { type: apiTags.Export, id: 'LIST' },
        ...(result?.exports?.map(({ id }) => ({
          type: apiTags.Export,
          id,
        })) || []),
      ],
    }),

    getExport: builder.query<Export, { id: string; websocket?: boolean }>({
      query: ({ id, websocket }) => ({
        url: `${DATASET_EXPORT_URL}/${id}`,
        params: { websocket },
      }),
      providesTags: (result, error, { id }) => [{ type: apiTags.Export, id }],
    }),

    deleteExport: builder.mutation<OkResp, string>({
      query: (id) => ({
        url: `${DATASET_EXPORT_URL}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: apiTags.Export, id: 'LIST' },
        { type: apiTags.Export, id },
        { type: apiTags.ExportContent, id },
        { type: apiTags.ExportMetadata, id },
      ],
    }),

    getExportContent: builder.query<
      { URL: string } | Blob,
      {
        id: string
        uri: string
        download: boolean
        expire: number
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${DATASET_EXPORT_URL}/${id}/content`,
        params,
        responseHandler: async (response) => (params.download ? response.blob() : response.json()),
      }),
      providesTags: (result, error, { id }) => [
        { type: apiTags.ExportContent, id },
        { type: apiTags.Export, id },
      ],
    }),

    getExportMetadata: builder.query<Record<string, unknown>, string>({
      query: (id) => ({
        url: `${DATASET_EXPORT_URL}/${id}/metadata`,
        responseHandler: async (response) => {
          const blob = await response.blob()
          return JSON.parse(await blob.text())
        },
      }),
      providesTags: (result, error, id) => [
        { type: apiTags.ExportMetadata, id },
        { type: apiTags.Export, id },
      ],
    }),
  }),
})

export const {
  useListExportsQuery,
  useCreateExportMutation,
  useQueryExportsQuery,
  useGetExportQuery,
  useDeleteExportMutation,
  useGetExportContentQuery,
  useGetExportMetadataQuery,
} = exportApi
